import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import LogoUploader from "../../sections/header/LogoUploader";
import LoginDropdown from "../../sections/header/LoginDropdown";
import { PiListBold, PiXBold, PiMagnifyingGlass, PiGlobe } from "react-icons/pi";

export default function UniversityHeader({
  content,
  onContentChange,
  isSelected,
  defaultLogo,
}: SectionContentProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const navItemsRaw = content.navItems || [
    { label: "Home", sectionId: "hero-default" },
    { label: "Pages", sectionId: "about-default" },
    { label: "Academics", sectionId: "features-default" },
    { label: "Event", sectionId: "contact-default" },
    { label: "Blog", sectionId: "blog-default" },
    { label: "Contact", sectionId: "contact-default" },
  ];

  const handleLogoChange = (newLogoUrl: string) => {
    if (onContentChange) {
      onContentChange({ ...content, logo: newLogoUrl });
    }
  };

  return (
    <>
      <div
        className={`w-full transition-all ${isSelected ? "ring-2 ring-blue-500 relative z-20" : ""}`}
        style={{
          fontFamily,
          backgroundColor: content?.colorPalette?.primary || "#7A1520",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between py-5">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-10"
              primaryColor="#FFFFFF"
              secondaryColor="#FFFFFF"
              companyName={content.companyName || "INSTACKS"}
              isEditing={!!onContentChange}
            />
          </div>

          {/* Center Nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navItemsRaw.map((item: any, idx: number) => {
              const navItem =
                typeof item === "string"
                  ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, "-") }
                  : item;

              const isCustomLink = navItem.sectionId === "custom";
              const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
              const isEditing = !!onContentChange;

              return (
                <a
                  key={idx}
                  href={isEditing ? undefined : targetHref}
                  target={isCustomLink && !isEditing ? "_blank" : undefined}
                  rel={isCustomLink ? "noopener noreferrer" : undefined}
                  onClick={(e) => {
                    if (isEditing) {
                      e.preventDefault();
                    } else if (!isCustomLink) {
                      e.preventDefault();
                      const el = document.getElementById(navItem.sectionId);
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth" });
                      }
                    }
                  }}
                >
                  <EditableText
                    value={navItem.label}
                    onChange={(val) => {
                      const newItems = [...(content.navItems || [])];
                      newItems[idx] = { ...navItem, label: val };
                      if (onContentChange)
                        onContentChange({ ...content, navItems: newItems });
                    }}
                    className="text-sm font-medium transition-all cursor-pointer uppercase tracking-wider whitespace-nowrap hover:opacity-80"
                    style={{
                      color: "rgba(255,255,255,0.9)",
                      letterSpacing: "0.05em",
                    }}
                    as="span"
                    placeholder="NAV"
                  />
                </a>
              );
            })}
          </nav>

          {/* Right side: icons + hamburger */}
          <div className="flex items-center gap-4 flex-shrink-0 ml-4">
            {/* Login button */}
            <LoginDropdown
              variant="text"
              className="hidden sm:inline-flex items-center justify-center px-4 py-2 text-xs font-semibold tracking-wider !text-white uppercase border border-white/40 transition-all hover:bg-white/10"
              style={{
                borderRadius: "0px",
                letterSpacing: "0.05em",
                fontFamily: "'Inter', sans-serif",
              }}
            />

            {/* Hamburger menu icon */}
            <button
              className="flex items-center text-white/80 hover:text-white transition-colors"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <div className="flex flex-col gap-1.5">
                <span className="block w-6 h-0.5 bg-current" />
                <span className="block w-4 h-0.5 bg-current ml-auto" />
                <span className="block w-6 h-0.5 bg-current" />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          style={{ fontFamily, backgroundColor: content?.colorPalette?.primary || "#7A1520" }}
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-8"
              primaryColor="#FFFFFF"
              secondaryColor="#FFFFFF"
              companyName={content.companyName || "INSTACKS"}
              isEditing={!!onContentChange}
            />
            <button
              className="p-2 text-white/80 hover:text-white transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <PiXBold className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
            <nav className="flex flex-col gap-4">
              {navItemsRaw.map((item: any, idx: number) => {
                const navItem =
                  typeof item === "string"
                    ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, "-") }
                    : item;

                const isCustomLink = navItem.sectionId === "custom";
                const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
                const isEditing = !!onContentChange;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    className="text-lg font-medium uppercase tracking-wider border-b border-white/10 pb-4 text-white/90 hover:text-white transition-colors"
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        setIsMobileMenuOpen(false);
                        const el = document.getElementById(navItem.sectionId);
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth" });
                        }
                      } else {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    {navItem.label}
                  </a>
                );
              })}
            </nav>
            {/* Mobile Login Button */}
            <LoginDropdown
              variant="text"
              className="mt-4 inline-flex items-center justify-center w-full py-3 text-sm font-semibold tracking-wider !text-white uppercase border border-white/40 transition-all hover:bg-white/10"
              style={{
                borderRadius: "0px",
                letterSpacing: "0.05em",
                fontFamily: "'Inter', sans-serif",
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
